import {
  OnboardingStep,
  SmbHospitalityOnboardingChecklistStep,
} from '@app/shared/onboarding/onboarding-checklist.model';
import { _ } from '@ngx-translate/core';

export enum FirstTouchModule {
  NEWS = 'NEWS',
  SCHEDULE = 'SCHEDULE',
  TIMESHEET = 'TIMESHEET',
  INSIGHTS = 'INSIGHTS',
  EMPLOYEES = 'EMPLOYEES',
  REPORTS = 'REPORTS',
}

export interface FirstTouchModuleConfig {
  urlRegExp?: RegExp;
  title: string;
  subtitle: string;
  description: string;
  buttonLabel: string;
  fallbackButtonLabel?: string;
  illustrationIsLocalized?: boolean;
  // To be defined by the service based on user's language
  // File paths must follow the pattern: src/assets/images/first-touch/<module-name-in-lower-case>[_<lang>].png
  // e.g., src/assets/images/first-touch/news_en.png for localized assets
  // or src/assets/images/first-touch/news.png for non-localized assets
  illustrationPath?: string;
}

export const FIRST_TOUCH_MODULES_SUPPORTED_LANGUAGES = ['en', 'nl', 'de'];

export const FIRST_TOUCH_MODULES: Record<FirstTouchModule, FirstTouchModuleConfig> = {
  [FirstTouchModule.NEWS]: {
    urlRegExp: /^\/communications\/news/,
    title: _('Keep everyone in the loop'),
    subtitle: _('News'),
    description: _('Share shift updates, announcements, and documents in one place — no more chasing messages.'),
    buttonLabel: _('Add your first Message'),
  },
  [FirstTouchModule.SCHEDULE]: {
    urlRegExp: /^\/schedule/,
    title: _('Get your week under control'),
    subtitle: _('Schedule'),
    description: _("Add your first shift to see who's working when. Publish a schedule your team can actually follow."),
    buttonLabel: _('Add your first shift'),
  },
  [FirstTouchModule.TIMESHEET]: {
    urlRegExp: /^\/timesheet/,
    title: _('Stop chasing hours. Start tracking them.'),
    subtitle: _('Timesheet'),
    description: _('Stop chasing timesheets. Start tracking hours now to fix payroll issues early and stay compliant.'),
    buttonLabel: _('Show me how'),
    fallbackButtonLabel: _('Add your first timesheet'),
  },
  [FirstTouchModule.INSIGHTS]: {
    urlRegExp: /^\/insights/,
    title: _('See where time and money are slipping'),
    subtitle: _('Performance'),
    description: _('Track labour cost trends, spot overstaffing, and act before issues pile up.'),
    buttonLabel: _('Explore Performance'),
  },
  [FirstTouchModule.EMPLOYEES]: {
    urlRegExp: /^\/employees/,
    title: _('One place to manage your team'),
    subtitle: _('Employees'),
    description: _('Manage contracts, documents, permissions, and team structure. All from one central dashboard.'),
    buttonLabel: _('Add employees'),
  },
  [FirstTouchModule.REPORTS]: {
    urlRegExp: /^\/reports/,
    title: _('Answer admin questions fast'),
    subtitle: _('Reports'),
    description: _(
      'Build reports to compare schedules, hours worked, absences, and payroll. Spot gaps, filter what matters, and export clean numbers for audits or planning.',
    ),
    buttonLabel: _('Explore reports'),
  },
};

// Mapping for onboarding steps that when started, should mark a first touch module as touched
export const ONBOARDING_STEP_FIRST_TOUCH_MODULE_MAPPING: Partial<Record<OnboardingStep, FirstTouchModule>> = {
  [SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT]: FirstTouchModule.SCHEDULE,
};

export const getFirstTouchModuleForStep = (step: OnboardingStep): FirstTouchModule | undefined =>
  ONBOARDING_STEP_FIRST_TOUCH_MODULE_MAPPING[step];
